import { useExportReportExportExamCourseAnalysisReportLazyQuery } from '@report/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
} from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, triggerFileDownload } from '@shared/utils';

import { EXAM_COURSE_TYPE_TEXT, MONTH_ORDER } from '../../constants';
import { useExamCourseAnalysisReportSearchParams } from '../../hooks';
interface ExamCourseAnalysisReportExportModalProps {
  open: boolean;
  total: number;
  onClose: () => void;
}

const MAXIMUM_EXPORT_COUNT = 50000;

export default function ExamCourseAnalysisReportExportModal(
  props: Readonly<ExamCourseAnalysisReportExportModalProps>,
) {
  const { open, onClose, total } = props;
  const { showSnackbar } = useSnackbar();

  const [
    { startMonth, startYear, endMonth, endYear, examCenter, examCourse, courseType, qConfigRef },
  ] = useExamCourseAnalysisReportSearchParams();

  const startedDate = dayjs()
    .set(
      'month',
      MONTH_ORDER.findIndex((month) => month === startMonth),
    )
    .set('year', startYear)
    .startOf('month');
  const endedDate = dayjs()
    .set(
      'month',
      MONTH_ORDER.findIndex((month) => month === endMonth),
    )
    .set('year', endYear)
    .endOf('month');
  const selectedDate = `${startedDate.format('MMMM BBBB')} - ${endedDate.format('MMMM BBBB')}`;

  const formattedExamCenter = examCenter.includes('all') ? 'Select All' : examCenter.join(', ');
  const formattedCourseType = courseType.map((type) => EXAM_COURSE_TYPE_TEXT[type]).join(', ');
  const formattedExamCourse = examCourse.includes('all') ? 'Select All' : examCourse.join(', ');
  const formattedQConfigRef = qConfigRef.includes('all') ? 'Select All' : qConfigRef.join(', ');

  const [getExamCourseAnalysisReportInput, { loading: isExporting }] =
    useExportReportExportExamCourseAnalysisReportLazyQuery();

  const handleExport = async () => {
    await getExamCourseAnalysisReportInput({
      variables: {
        exportExamCourseAnalysisReportInput: {
          filter: {
            startedAt: startedDate.toDate(),
            endedAt: endedDate.toDate(),
            examCourseCodes: examCourse.includes('all') ? null : examCourse,
            examCenterCodes: examCenter.includes('all') ? null : examCenter,
            examCourseTypes: courseType,
            qConfigRefs: qConfigRef.includes('all') ? null : qConfigRef.map((ref) => Number(ref)),
          },
          order: {
            field: 'examId',
            type: ORDER_TYPE.asc,
          },
        },
      },
      onCompleted: (data) => {
        const signedUrl = data.reportExportExamCourseAnalysisReport?.signedUrl ?? '';
        if (!signedUrl) {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const isExceedingMaximumExportCount = total > MAXIMUM_EXPORT_COUNT;
  const disableExport = isExporting || isExceedingMaximumExportCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose}>ยืนยันการส่งออกข้อมูล</DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(3) })}>
          {isExceedingMaximumExportCount ? (
            <Alert severity='error'>
              <Text>
                จำกัดการดาวน์โหลดสูงสุด {MAXIMUM_EXPORT_COUNT.toLocaleString()} รายการต่อครั้ง
              </Text>
            </Alert>
          ) : null}
          <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Date:
            </Text>
            <Text>{selectedDate}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Exam Center:
            </Text>
            <Text>{formattedExamCenter}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Course Type:
            </Text>
            <Text>{formattedCourseType}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Exam Course:
            </Text>
            <Text>{formattedExamCourse}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              QConfig Ref:
            </Text>
            <Text>{formattedQConfigRef}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              รวมทั้งหมด:
            </Text>
            <Text>{total} รายการ</Text>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            onClick={handleExport}
            loading={isExporting}
            disabled={disableExport}
          >
            ส่งออกข้อมูล
          </Button>
        </Flex>
      </DialogActions>
    </Dialog>
  );
}
